import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, Loader2, Check, AlertCircle, ArrowLeft, Heart } from 'lucide-react';
import api from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

/**
 * Page de désinscription publique — RGPD-compliant.
 *
 * Acceptée via :
 *   /newsletter/desinscription?email=xxx@yyy.com  (lien depuis email)
 *   /newsletter/desinscription                    (saisie manuelle)
 *
 * Backend : POST /api/public/newsletter/unsubscribe
 */
export default function NewsletterUnsubscribe() {
  const [params] = useSearchParams();
  const initialEmail = params.get('email') || '';
  const [email, setEmail] = useState(initialEmail);
  const [status, setStatus] = useState('idle'); // idle | loading | success | not_found | error
  const [serverMsg, setServerMsg] = useState('');

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setStatus('error');
      setServerMsg('Merci de saisir une adresse email valide.');
      return;
    }
    setStatus('loading');
    setServerMsg('');
    try {
      const res = await api.newsletterUnsubscribe(email.trim().toLowerCase());
      if (res?.status === 'unsubscribed') {
        setStatus('success');
        setServerMsg(res?.message || 'Désinscription confirmée.');
      } else if (res?.status === 'not_found') {
        setStatus('not_found');
        setServerMsg(res?.message || "Cet email n'est pas dans notre liste.");
      } else {
        setStatus('error');
        setServerMsg(res?.message || 'Réponse inattendue du serveur.');
      }
    } catch (err) {
      setStatus('error');
      setServerMsg(err?.message || 'Une erreur s\u2019est produite.');
    }
  };

  // Si email pré-rempli via URL → on auto-soumet (UX 1-clic depuis email marketing)
  // mais on laisse l'utilisateur confirmer en cliquant pour éviter les pré-fetchs anti-spam
  // qui désinscrivent sans intention.

  return (
    <div className="max-w-xl mx-auto px-4 py-10 sm:py-16" data-testid="newsletter-unsubscribe-page">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors mb-6"
        data-testid="unsubscribe-back-home"
      >
        <ArrowLeft className="w-4 h-4" />
        Retour à l'accueil
      </Link>

      <div className="mc-card p-6 sm:p-8">
        {/* Icone header */}
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ background: 'hsl(var(--primary-soft))' }}>
          <Mail className="w-7 h-7 text-[hsl(var(--primary))]" />
        </div>

        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.32em] text-[hsl(var(--primary))] mb-3">
            <span className="h-px w-6 bg-[hsl(var(--primary))]" />
            Newsletter
          </div>
          <h1 className="text-2xl sm:text-3xl font-black leading-tight tracking-tight" data-testid="unsubscribe-title">
            Confirmer ta <span className="text-[hsl(var(--primary))]">désinscription</span>
          </h1>
          <p className="mt-3 text-sm text-foreground/70 leading-relaxed">
            Tu ne recevras plus aucun email de la newsletter Code 2 Roues. Tu pourras te ré-inscrire à tout moment depuis l'accueil.
          </p>
        </div>

        {status === 'success' ? (
          <div className="text-center py-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-500/10 text-green-600 dark:text-green-400 mb-3">
              <Check className="w-6 h-6" />
            </div>
            <p className="font-bold text-base mb-1" data-testid="unsubscribe-success-title">Désinscription confirmée</p>
            <p className="text-sm text-muted-foreground">{serverMsg}</p>
            <p className="mt-5 text-sm text-foreground/80 inline-flex items-center gap-1.5">
              <Heart className="w-4 h-4 text-[hsl(var(--primary))]" />
              Merci d'avoir suivi Code 2 Roues. Reviens quand tu veux !
            </p>
            <Link to="/">
              <Button variant="outline" className="mt-5" data-testid="unsubscribe-back-btn">
                Retour à l'accueil
              </Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="unsubscribe-form">
            <div className="space-y-1.5">
              <Label htmlFor="unsub-email" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Ton adresse email</Label>
              <Input
                id="unsub-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="toi@exemple.fr"
                required
                disabled={status === 'loading'}
                data-testid="unsubscribe-input-email"
              />
            </div>

            {status === 'error' && serverMsg && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-[hsl(var(--primary))]/8 text-[hsl(var(--primary))] text-sm" data-testid="unsubscribe-error">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{serverMsg}</span>
              </div>
            )}
            {status === 'not_found' && serverMsg && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 text-sm" data-testid="unsubscribe-not-found">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{serverMsg} (peut-être déjà désinscrit)</span>
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={status === 'loading'}
              data-testid="unsubscribe-submit-btn"
            >
              {status === 'loading' ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Désinscription en cours…</>
              ) : (
                'Confirmer ma désinscription'
              )}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
              Conformément au RGPD, ton adresse reste conservée 30 jours en base puis est supprimée.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
